import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Avatar, Box, IconButton, Menu, MenuItem, Typography, Divider, ListItemIcon, Chip,
} from '@mui/material';
import {
  Person, Settings, Logout, ConfirmationNumber, History, People, Star,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { colors, designTokens } from '../../theme/theme';

export default function UserMenu() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const initials = ((user?.first_name?.[0] || '') + (user?.last_name?.[0] || '')).toUpperCase() || '?';
  const displayName = [user?.first_name, user?.last_name].filter(Boolean).join(' ') || 'CoffeeMates User';

  const handleLogout = () => {
    setAnchorEl(null);
    logout();
    navigate('/');
  };

  const menuItems = [
    { label: 'My Profile', icon: <Person fontSize="small" />, path: '/profile' },
    { label: 'My Vouchers', icon: <ConfirmationNumber fontSize="small" />, path: '/vouchers' },
    { label: 'Event History', icon: <History fontSize="small" />, path: '/vouchers' },
    { label: 'Connections', icon: <People fontSize="small" />, path: '/profile/connections' },
    { divider: true },
    { label: 'Settings', icon: <Settings fontSize="small" />, path: '/profile' },
  ];

  return (
    <>
      <IconButton
        aria-label="Open user menu"
        aria-haspopup="true"
        aria-expanded={Boolean(anchorEl)}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{ p: 0, ml: 1 }}
      >
        <Avatar
          src={user?.avatar_url || undefined}
          sx={{
            width: 36, height: 36,
            bgcolor: colors.primary.main, color: 'white',
            fontSize: '0.85rem', fontWeight: 700,
          }}
        >
          {initials}
        </Avatar>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          sx: { width: 240, mt: 1, borderRadius: `${designTokens.radius.md}px`, boxShadow: designTokens.shadow.lg },
        }}
      >
        {/* User header */}
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#FFFFFF' }}>{displayName}</Typography>
          <Typography variant="caption" sx={{ color: colors.neutral[500] }}>
            {user?.email || user?.phone || ''}
          </Typography>
          {user?.reputation_score != null && (
            <Chip
              icon={<Star sx={{ fontSize: 12, color: `${colors.warning.main} !important` }} />}
              label={user.reputation_score}
              size="small"
              sx={{
                mt: 0.5,
                fontSize: '0.7rem',
                height: 20,
                bgcolor: colors.warning.subtle,
                color: colors.warning.main,
                borderRadius: `${designTokens.radius.sm}px`,
              }}
            />
          )}
        </Box>

        <Divider />

        {menuItems.map((item, i) =>
          'divider' in item ? <Divider key={i} /> : (
            <MenuItem key={i} onClick={() => { setAnchorEl(null); navigate(item.path!); }}
              sx={{ py: 1, fontSize: '0.9rem' }}>
              <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
              {item.label}
            </MenuItem>
          )
        )}

        <Divider />

        <MenuItem onClick={handleLogout} sx={{ py: 1, color: 'error.main', fontSize: '0.9rem' }}>
          <ListItemIcon sx={{ minWidth: 36, color: 'error.main' }}><Logout fontSize="small" /></ListItemIcon>
          Sign Out
        </MenuItem>
      </Menu>
    </>
  );
}
